import type { LanguagePack } from '../types';

export const arPack: LanguagePack<'ar'> = {
  code: 'ar',
  name: { native: 'العربية', english: 'Arabic' },
  script: 'arabic',
  dir: 'rtl',
  locales: { date: 'ar-SA-u-ca-gregory-nu-latn', number: 'ar-SA-u-nu-latn' },

  detection: {
    scriptRanges: ['\\u0600-\\u06FF', '\\u0750-\\u077F', '\\uFB50-\\uFDFF', '\\uFE70-\\uFEFF'],
    sttCodes: ['ara', 'arb', 'ar'],
    explicitSwitchPhrases: [
      /^(?:العربية|عربي|بالعربي|arabic)$/iu,
      /(?:تكلم|كلمني|تحدث|اكتب|رد|خاطبني)\s*(?:معي|معاي|علي)?\s*(?:بال)?عرب(?:ي|ية)/u,
      /\b(?:speak|reply|talk|write|answer)\s+(?:to me\s+)?(?:in\s+)?arabic\b/i,
      /\barabic\s+(?:please|pls)\b/i,
    ],
    greetingWords: [
      'السلام عليكم',
      'سلام عليكم',
      'مرحبا',
      'مرحبًا',
      'هلا',
      'هلا والله',
      'الو',
      'ألو',
      'اهلا',
      'أهلا',
      'اهلين',
      'أهلين',
      'يا هلا',
      'صباح الخير',
      'مساء الخير',
    ],
  },

  voice: {
    styleNote: [
      'Write the plain spoken Arabic a Saudi office employee types on WhatsApp — Saudi white dialect, understood from Jeddah to Dammam. Office-warm: friendly and clear, never stiff, never street slang.',
      'Not formal MSA and not translated English: no يرجى / نود إفادتكم / عزيزي العميل / أخبرني / تفضّل بإخباري.',
      'Saudi words where they fit: تبي، عشان، الحين، أبشر، حياك الله، تمام، ولا يهمك. Sparingly — not on every line.',
      'Ask for something with أرسل لي / ممكن تزودني بـ — never ابعث / ابعتلي. For an official number (unified number, CR) use ما هو / ممكن تزودني, not وش.',
      'Never another dialect: no شنو، شكو، عايز، كده، شو، هلق، منيح، باش، بزاف.',
      'Singular أنت to one person, even for a company (حسابك، تقدر) — never أنتم / حضرتك / سيادتكم. Masculine unless the user is clearly a woman.',
      'Arabic script only. Western digits (743198). Plates and numbers exactly as on the card.',
      'Document names as drivers say them: الهوية، الإقامة، الاستمارة، الرخصة، وثيقة النقل.',
      'One or two short lines, one ask at the end, at most one emoji.',
    ].join('\n'),
    examples: [
      {
        scenario: 'greeting',
        turns: [
          { role: 'user', text: 'السلام عليكم' },
          {
            role: 'agent',
            text: 'وعليكم السلام، حياك الله 👋 معك وثيقة بلس، نطلع لك وثيقة النقل من الواتساب مباشرة. أنت شركة نقل، وسيط شحن، ولا سائق؟',
          },
        ],
        note: 'First reply on the line: who we are in one line, then the one open question.',
      },
      {
        scenario: 'greeting',
        turns: [
          { role: 'user', text: 'هلا' },
          { role: 'agent', text: 'هلا والله 👋 معك وثيقة بلس للوثائق الحكومية. أنت شركة نقل، وسيط، ولا سائق؟' },
        ],
        note: 'A bare هلا gets هلا back, not السلام عليكم.',
      },
      {
        scenario: 'returning_user',
        turns: [
          { role: 'user', text: 'السلام عليكم' },
          { role: 'agent', text: 'وعليكم السلام، هلا والله. من وين لوين اليوم؟' },
        ],
        note: 'Account and driver on file: no intro, straight to the lane.',
      },
      {
        scenario: 'cards_photo',
        turns: [
          { role: 'user', text: '[photos: iqama + istimara]' },
          {
            role: 'agent',
            text: 'حياك الله، معك وثيقة بلس 👋 قرأت إقامة محمد خان والاستمارة. أنت شركة نقل، وسيط، ولا هو أنت السائق؟',
          },
        ],
        note: 'Cards never decide the account type — ask it once, keep what was read.',
      },
      {
        scenario: 'account_type',
        turns: [
          { role: 'user', text: 'عندي شركة نقل' },
          { role: 'agent', text: 'أبشر. ما اسم شركتك؟ عشان أفتح لك الحساب.' },
        ],
      },
      {
        scenario: 'account_type',
        turns: [
          { role: 'user', text: 'انا سواق' },
          { role: 'agent', text: 'تمام. أرسل لي صورة إقامتك والاستمارة 📷.' },
        ],
        note: 'A driver is never asked his name — it comes off the iqama.',
      },
      {
        scenario: 'ask_identity',
        turns: [
          { role: 'user', text: 'ابي وثيقة من جدة للرياض بكرة' },
          { role: 'agent', text: 'تمام. أرسل لي صورة الهوية أو الإقامة والاستمارة، والباقي علي.' },
        ],
        note: 'Lane and date already given — never ask for them again.',
      },
      {
        scenario: 'waybill_request',
        turns: [
          { role: 'user', text: 'رياض جدة' },
          { role: 'agent', text: '[stages the waybill — the card is the reply, no words]' },
        ],
        note: 'Two cities and a saved driver are enough: stage, do not interview.',
      },
      {
        scenario: 'waybill_issued',
        turns: [{ role: 'agent', text: 'تمت ✅ وثيقتك رقم 743198. الله يوفقك في المشوار.' }],
      },
      {
        scenario: 'waybill_edit',
        turns: [
          { role: 'user', text: 'غير المستلم مؤسسة النور' },
          { role: 'agent', text: '[stages the edit — the card is the reply]' },
        ],
      },
      {
        scenario: 'price_question',
        turns: [
          { role: 'user', text: 'بكم الوثيقة؟' },
          { role: 'agent', text: 'أول وثيقة علينا، وبعدها الوثيقة بـ {price}. أطلع لك وحدة الحين؟' },
        ],
        note: '{price} is copied from get_subscription_options — never from memory.',
      },
      {
        scenario: 'thanks_closer',
        turns: [
          { role: 'user', text: 'مشكور' },
          { role: 'agent', text: '[react 🤝]' },
        ],
        note: 'The task is done; a reaction is the whole answer.',
      },
      {
        scenario: 'thanks_closer',
        turns: [
          { role: 'user', text: 'الله يعطيك العافية' },
          { role: 'agent', text: 'الله يعافيك، بالتوفيق 🙏' },
        ],
        note: 'A blessing gets a blessing back — short, no pitch.',
      },
      {
        scenario: 'sticker',
        turns: [
          { role: 'user', text: '[sticker: thumbs up, text "تم"]' },
          { role: 'agent', text: '[react 👍]' },
        ],
      },
      {
        scenario: 'error',
        turns: [
          {
            role: 'agent',
            text: 'بيان رفض الوثيقة: رقم اللوحة ما يطابق الاستمارة. أرسل لي صورة الاستمارة 📷 أو اكتب اللوحة الصحيحة وأعيد الإصدار.',
          },
        ],
        note: 'Say what failed in plain words and the one thing that fixes it.',
      },
    ],
    glossary: {
      waybill: 'وثيقة النقل',
      national_id: 'الهوية',
      iqama: 'الإقامة',
      vehicle_registration: 'الاستمارة',
      driver_license: 'الرخصة',
      plate: 'اللوحة',
      trip: 'المشوار',
    },
    reviewStatus: 'draft',
  },
};
